import { Scroll, ScrollControls, useScroll } from "@react-three/drei";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { RadioPart } from "./scene/parts/RadioPart";
import { ScrollLayoutMeasurer } from "./scene/ScrollLayoutMeasurer";
import {
  radioScrollLayout,
  useScrollLayout,
  pagesFromMeasurement,
} from "./scene/scrollLayout";
import {
  deriveRadioTextWindows,
  DEFAULT_RADIO_MEASUREMENT,
} from "./scene/parts/radioScrollWindows";
import { Footer } from "./Footer";
import { ScrollElBridge } from "./scene/ScrollElBridge";
import { Button } from "./ui/Button";
import { Price } from "./ui/Price";
import { Diamond } from "./ui/Diamond";
import { StackedChevrons } from "./ui/StackedChevrons";
import { MarkdownBody } from "./scene/MarkdownBody";
import { Modal } from "./ui/Modal";
import { SpecsBody, FaqBody } from "./RadioDetailBodies";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../app/queryClient";
import { useContent, text, deck, usdRate } from "../hooks/useContent";
import { useTranslation } from "react-i18next";
import { formatAmount, currencySymbol, usdAmount } from "../utils/formatPrice";
import { DeckCheckoutModal } from "./shop/DeckCheckoutModal";
import { useBreakpoint } from "../hooks/useBreakpoint";
import type { SiteContent } from "../api/content";

/** The spirit box rides the existing deck checkout flow — the backend looks
 *  any content.Deck up by key, and the spirit-box row carries price/labels. */
const SPIRIT_BOX_KEY = "spirit-box";

/** Feature sections rendered as a uniform CMS-driven column. `cta` marks
 *  sections that end in an order button: "buy" reuses the deck buy label,
 *  otherwise the named radio.* text key supplies the label. */
// Consolidated tour (migration content/0050): sweep now covers band + speed
// + direction; charging now covers USB-C + AUX + no-Bluetooth. The merged-away
// keys (speed/direction/aux/nobluetooth) still exist in the CMS but are no
// longer shown — the spec table + FAQ carry the full detail.
const FEATURE_SECTIONS: {
  key: string;
  ctaTextKey?: string;
  buyCta?: boolean;
}[] = [
  { key: "what" },
  { key: "noai", ctaTextKey: "radio.noai.cta" },
  { key: "design" },
  { key: "antenna", buyCta: true },
  { key: "sweep" },
  { key: "noise" },
  { key: "lock" },
  { key: "temperature" },
  { key: "backlight" },
  { key: "battery" },
  { key: "charging" },
];

/** Number of feature panels (sN anchors s1..sN). The specs/how-to anchors that
 *  follow are derived from it (s{N+1}, s{N+2}) — the side-nav rail reads these
 *  to target the right sections, so it's exported rather than re-counted. */
export const RADIO_FEATURE_COUNT = FEATURE_SECTIONS.length;

const HOWTO_STEPS = 7;

type SectionSide = "left" | "right";

/** Sections zigzag: odd section numbers (s1, s3, …) sit left, even right.
 *  The 3D model travels the opposite (empty) half — see RadioPart. */
const sideFor = (n: number): SectionSide => (n % 2 === 1 ? "left" : "right");

/** Desktop fixed-panel set: the 15 features plus the three closing plain
 *  sections, each a services-style card (Diamond + t-section + t-subtitle +
 *  markdown body). Specs (s16) and how-to (s17) are too tall for a fixed
 *  panel and stay in the scroll flow. "chips" renders the who-it's-for
 *  bullets as pill tags. */
const PANEL_SECTIONS: {
  n: number;
  key: string;
  num?: string;
  ctaTextKey?: string;
  buyCta?: boolean;
  bodyMode: "full" | "chips";
}[] = [
  ...FEATURE_SECTIONS.map((s, i) => ({
    n: i + 1,
    key: s.key,
    num: String(i + 1).padStart(2, "0"),
    ctaTextKey: s.ctaTextKey,
    buyCta: s.buyCta,
    bodyMode: "full" as const,
  })),
  { n: FEATURE_SECTIONS.length + 3, key: "whofor", bodyMode: "chips" },
  { n: FEATURE_SECTIONS.length + 4, key: "included", bodyMode: "full" },
  { n: FEATURE_SECTIONS.length + 5, key: "notice", bodyMode: "full" },
];

/* ── decks-style crossfade helpers ── */

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);

function fadeVis(
  offset: number,
  inStart: number,
  inEnd: number,
  outStart: number,
  outEnd: number,
) {
  const i = clamp01((offset - inStart) / (inEnd - inStart));
  const o = clamp01((offset - outStart) / (outEnd - outStart));
  return easeOutQuart(i) * (1 - easeOutQuart(o));
}

/** Reveals its children once they scroll into view (one-shot): the copy
 *  fades/rises in as the traveling model parks beside it, so mid-flight the
 *  next section isn't already sitting on screen waiting. Falls back to
 *  always-visible without IntersectionObserver (jsdom) and for
 *  prefers-reduced-motion. */
function Reveal({ children }: { children: React.ReactNode }) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      // Fire once the copy is genuinely inside the viewport (not peeking at
      // the bottom edge) — that's when the model has arrived beside it.
      { rootMargin: "0px 0px -25% 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0 ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      {children}
    </div>
  );
}

/** Alternating half-width wrapper: copy in one grid column, the other left
 *  empty for the traveling model. Collapses to a single centered column on
 *  mobile/tablet (where the model doesn't travel). */
function SideColumn({
  side,
  children,
}: {
  side: SectionSide;
  children: React.ReactNode;
}) {
  return (
    <div className="max-w-6xl mx-auto w-full md:grid md:grid-cols-2 md:gap-10 items-center">
      {side === "right" ? <div className="hidden md:block" /> : null}
      <div className="max-w-2xl max-md:mx-auto">
        <Reveal>{children}</Reveal>
      </div>
    </div>
  );
}

/* ── Section building blocks ── */

/** Type tone: "light" = dark-blue-on-clouds (default flow), "dark" =
 *  white-on-dusk (the desktop feature tour sinks into the services dusk
 *  atmosphere — see RadioPart's dusk blend). */
type SectionTone = "light" | "dark";

/** Mirrors the home services card typography exactly (the canonical
 *  white-on-dusk type): `t-section` title + `t-subtitle` line; the light
 *  tone swaps in the primary-blue equivalents. `uppercase` is applied in
 *  class (radio CMS titles are sentence case; services carry caps in data). */
function SectionHeading({
  title,
  subtitle,
  tone = "light",
}: {
  title: string;
  subtitle?: string;
  tone?: SectionTone;
}) {
  return (
    <>
      <h2
        className={`m-0 t-section uppercase whitespace-pre-line ${
          tone === "dark" ? "text-white" : "text-primary"
        }`}
      >
        {title}
      </h2>
      {subtitle ? (
        <div className={`mt-4 ${tone === "dark" ? "t-subtitle" : "t-subtitle-blue"}`}>
          {subtitle}
        </div>
      ) : null}
    </>
  );
}

/** Parses a CMS markdown bullet list ("- item;") into plain labels. */
function bulletsToChips(body: string): string[] {
  return body
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("- "))
    .map((line) => line.replace(/^- /, "").replace(/[;.]$/, ""));
}

/** One section's copy block — shared between the mobile/tablet scroll flow
 *  and the desktop fixed crossfade panels. */
function FeatureCopy({
  content,
  sectionKey,
  ctaLabel,
  onOrder,
  num,
  tone = "light",
  bodyMode = "full",
}: {
  content: SiteContent | undefined;
  sectionKey: string;
  ctaLabel?: string;
  onOrder: () => void;
  /** Numbered diamond marker ("01"…) — gives the long feature column the
   *  site's editorial rhythm; plain sections (who-for etc.) omit it. */
  num?: string;
  tone?: SectionTone;
  /** "full" — markdown body (services-style card); "chips" — body bullets
   *  rendered as pill tags (who-it's-for). */
  bodyMode?: "full" | "chips";
}) {
  const dark = tone === "dark";
  return (
    <>
      {num ? (
        <Diamond n={num} tone={dark ? "white" : "primary"} className="mb-7" />
      ) : null}
      <SectionHeading
        title={text(content, `radio.${sectionKey}.title`)}
        subtitle={text(content, `radio.${sectionKey}.subtitle`)}
        tone={tone}
      />
      {bodyMode === "full" ? (
        <div className="mt-8 max-w-2xl">
          <MarkdownBody
            body={text(content, `radio.${sectionKey}.body`)}
            surface={dark ? "blue" : "light"}
          />
        </div>
      ) : null}
      {bodyMode === "chips" ? (
        <div className="mt-9 flex flex-wrap gap-2.5">
          {bulletsToChips(text(content, `radio.${sectionKey}.body`)).map(
            (chip) => (
              <span
                key={chip}
                className={`rounded-full border px-4 py-1.5 text-tag uppercase tracking-eyebrow ${
                  dark
                    ? "border-white/35 text-white/90"
                    : "border-primary/30 text-primary/85"
                }`}
              >
                {chip}
              </span>
            ),
          )}
        </div>
      ) : null}
      {ctaLabel ? (
        // pointer-events-auto: inside the fixed crossfade panels the
        // container is pointer-events:none (wheel must fall through to the
        // scroll container below), so the button re-enables interactivity
        // itself. Hidden panels are visibility:hidden — no stray hits.
        <div className="mt-9 pointer-events-auto inline-block">
          <Button
            variant="solid"
            tone="muted"
            surface={tone === "dark" ? "dark" : "light"}
            as="button"
            type="button"
            onClick={onOrder}
          >
            {ctaLabel}
          </Button>
        </div>
      ) : null}
    </>
  );
}

function FeatureSection({
  content,
  sectionKey,
  ctaLabel,
  onOrder,
  first,
  anchorKey,
  num,
  side,
}: {
  content: SiteContent | undefined;
  sectionKey: string;
  ctaLabel?: string;
  onOrder: () => void;
  /** First content section — carries the #features id for hero CTAs. */
  first?: boolean;
  /** Measured anchor (s1, s2, …) — waypoint of the 3D model's travel path. */
  anchorKey?: string;
  num?: string;
  side: SectionSide;
}) {
  return (
    <section
      id={first ? "features" : undefined}
      data-scroll-anchor={anchorKey}
      className="px-6 py-[12vh] md:py-[8vh] md:min-h-screen md:flex md:items-center pointer-events-auto"
    >
      <SideColumn side={side}>
        {/* The in-flow sections (tablet/mobile) share the desktop panels'
            white-on-dusk palette — /spirit-box is nocturnal on every breakpoint. */}
        <FeatureCopy
          content={content}
          sectionKey={sectionKey}
          ctaLabel={ctaLabel}
          onOrder={onOrder}
          num={num}
          tone="dark"
        />
      </SideColumn>
    </section>
  );
}

/** Desktop: fixed crossfading text panels (the decks deck-panel pattern).
 *  Portaled to a body-owned host — position:fixed breaks inside drei's
 *  <Scroll html> (transformed ancestor). Visibility is driven per-frame from
 *  the measured panel windows; only one panel is readable at a time. */
function RadioTextPanels({
  content,
  spiritBox,
  onOrder,
  portalHost,
  isExiting,
}: {
  content: SiteContent | undefined;
  spiritBox: ReturnType<typeof deck>;
  onOrder: () => void;
  portalHost: HTMLDivElement | null;
  isExiting?: boolean;
}) {
  const scroll = useScroll();
  const layout = useScrollLayout(radioScrollLayout);
  const windows = React.useMemo(
    () =>
      (layout && deriveRadioTextWindows(layout)) ??
      deriveRadioTextWindows(DEFAULT_RADIO_MEASUREMENT)!,
    [layout],
  );
  const refs = React.useRef<(HTMLDivElement | null)[]>([]);
  const isExitingRef = React.useRef(false);
  isExitingRef.current = !!isExiting;

  React.useEffect(() => {
    // Per-panel last-applied visibility: with 18 panels, unconditional style
    // writes every frame trigger style recalc across the whole overlay and
    // make the scroll stutter. At most ~2 panels are mid-fade at once, so
    // skipping unchanged panels cuts the per-frame work to almost nothing.
    const lastVis = new Float32Array(PANEL_SECTIONS.length).fill(-1);
    let raf = 0;
    const tick = () => {
      if (!isExitingRef.current) {
        const offset = scroll?.offset ?? 0;
        for (let i = 0; i < PANEL_SECTIONS.length; i++) {
          const el = refs.current[i];
          const w = windows[PANEL_SECTIONS[i].n];
          if (!el || !w) continue;
          const v =
            Math.round(fadeVis(offset, w.in, w.inEnd, w.out, w.outEnd) * 1000) /
            1000;
          if (v === lastVis[i]) continue;
          lastVis[i] = v;
          el.style.opacity = String(v);
          el.style.transform = `scale(${0.94 + 0.06 * v})`;
          // NOTE: the panel container stays pointer-events:none at all times.
          // It lives in a body portal OUTSIDE drei's scroll container, so an
          // interactive panel would swallow wheel events and the page would
          // stop scrolling under the cursor. Only the CTA button re-enables
          // pointer events (see FeatureCopy).
          // A compositor layer only while actually fading — 18 permanent
          // will-change layers of text are exactly what lags the scroll.
          el.style.willChange = v > 0 && v < 1 ? "opacity, transform" : "";
          // Fully faded panels must also be visibility-hidden: they overlap,
          // and a hidden-but-rendered panel would still take keyboard focus
          // on its CTA button.
          el.style.visibility = v > 0.001 ? "visible" : "hidden";
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [scroll, windows]);

  if (!portalHost) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[5] pointer-events-none">
      {PANEL_SECTIONS.map((p, i) => {
        return (
          <div
            key={p.key}
            className="absolute inset-x-0 top-1/2 -translate-y-1/2 px-6"
          >
            {/* Copy always takes the right column — the model is parked on
                the left pedestal for the whole page (showcase pattern). */}
            <div className="max-w-6xl mx-auto w-full grid grid-cols-2 gap-10 items-center">
              <div />
              <div
                ref={(el) => {
                  refs.current[i] = el;
                }}
                style={{
                  opacity: 0,
                  transform: "scale(0.94)",
                  transformOrigin: "left center",
                  pointerEvents: "none",
                  visibility: "hidden",
                }}
                className="max-w-2xl"
              >
                <FeatureCopy
                  content={content}
                  sectionKey={p.key}
                  num={p.num}
                  tone="dark"
                  bodyMode={p.bodyMode}
                  onOrder={onOrder}
                  ctaLabel={
                    p.buyCta
                      ? spiritBox?.buyLabel
                      : p.ctaTextKey
                        ? text(content, p.ctaTextKey)
                        : undefined
                  }
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>,
    portalHost,
  );
}

function HowToSection({
  content,
  anchorKey,
  side,
  tone = "light",
}: {
  content: SiteContent | undefined;
  anchorKey?: string;
  side: SectionSide;
  tone?: SectionTone;
}) {
  const dark = tone === "dark";
  const steps = Array.from({ length: HOWTO_STEPS }, (_, i) => i + 1);
  return (
    <section
      data-scroll-anchor={anchorKey}
      className="px-6 py-[14vh] md:min-h-screen md:flex md:items-start md:pt-[16vh] pointer-events-auto"
    >
      <SideColumn side={side}>
        <SectionHeading
          title={text(content, "radio.howto.title")}
          subtitle={text(content, "radio.howto.subtitle")}
          tone={tone}
        />
        <ol className="mt-9 m-0 p-0 list-none space-y-6">
          {steps.map((n) => (
            <li key={n} className="flex items-start gap-4">
              <span
                className={`flex-none mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full text-[13px] font-medium tracking-[0.06em] ${
                  dark
                    ? "border border-white/40 text-white"
                    : "border border-primary/35 text-primary"
                }`}
              >
                {String(n).padStart(2, "0")}
              </span>
              <div className="pt-1">
                <div
                  className={`text-[15px] font-medium leading-snug ${
                    dark ? "text-white" : "text-primary"
                  }`}
                >
                  {text(content, `radio.howto.step${n}.title`)}
                </div>
                <p
                  className={`text-text mt-1.5 leading-relaxed ${
                    dark ? "text-white/70" : "text-primary/75"
                  }`}
                >
                  {text(content, `radio.howto.step${n}.body`)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </SideColumn>
    </section>
  );
}

/* ── HTML overlay ── */

function RadioHtmlContent({
  portalHost,
  isExiting,
}: {
  portalHost: HTMLDivElement | null;
  isExiting?: boolean;
}) {
  const { t } = useTranslation("ui");
  const { data: content } = useContent();
  const spiritBox = deck(content, SPIRIT_BOX_KEY);
  const rate = usdRate(content);
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";
  // Desktop pairs the traveling model with fixed crossfading text panels;
  // tablet/mobile keep the in-flow sections (no traveling model there).
  const panelMode = breakpoint === "desktop";
  const scroll = useScroll();
  const [checkoutOpen, setCheckoutOpen] = React.useState(false);
  const openCheckout = React.useCallback(() => setCheckoutOpen(true), []);
  const [specsOpen, setSpecsOpen] = React.useState(false);
  const [faqOpen, setFaqOpen] = React.useState(false);
  const [chevronsVisible, setChevronsVisible] = React.useState(true);

  React.useEffect(() => {
    const el = scroll?.el as HTMLElement | undefined;
    if (!el) return;
    const update = () => {
      // Chevrons live in the hero — show only while at the very top.
      setChevronsVisible(el.scrollTop <= 8);
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    return () => el.removeEventListener("scroll", update);
  }, [scroll]);

  const scrollToFeatures = React.useCallback(() => {
    const el = scroll?.el as HTMLElement | undefined;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    // Jump to the measured first-section anchor (s1). drei translates the
    // overlay by (pages−1)·vh·offset while maxScroll = pages·vh ⟹
    // scrollTop = anchorPx · pages/(pages−1). Fallback: one viewport.
    const m = radioScrollLayout.get();
    const pages = m && m.vh > 0 ? m.contentHeight / m.vh : 0;
    const target =
      m && pages > 1 && m.anchors.s1 != null
        ? Math.max(0, Math.min(max, (m.anchors.s1 * pages) / (pages - 1)))
        : Math.min(max, el.clientHeight);
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ top: target, behavior: reduce ? "auto" : "smooth" });
  }, [scroll]);

  return (
    <main className="w-screen pointer-events-none">
      {/* Hero — copy on the left, the 3D radio floats in the right half
          (mobile: radio rides the top third, copy stacks below). */}
      {/* pt clears the fixed navbar; the hero is allowed to grow past 100vh
          when the copy is taller than the viewport (content-driven scroll
          absorbs the extra height). */}
      <div className="relative min-h-screen flex items-center px-6 pt-28 pb-24 pointer-events-auto">
        <div className="max-w-6xl mx-auto w-full md:grid md:grid-cols-2 md:gap-10 items-center">
          {/* svh, not vh: iOS Safari's vh ignores the browser bars, which
              pushed the scroll cue below the visible fold on phones. */}
          <div className={isMobile ? "pt-[12svh] pb-10 text-center" : "text-left"}>
            <h1 className="t-hero t-hero--radio-fit t-hero--on-dusk">
              {text(content, "radio.hero.title")}
            </h1>
            <div className="mt-4 t-subtitle t-subtitle--on-dusk">
              {text(content, "radio.hero.subtitle")}
            </div>
            <div className={isMobile ? "mt-6 text-left" : "mt-8"}>
              <MarkdownBody
                body={text(content, "radio.hero.body")}
                surface="blue"
              />
            </div>
            <div
              className={`flex ${
                isMobile
                  ? "mt-7 flex-col items-stretch gap-4"
                  : "mt-9 flex-wrap items-center gap-3"
              }`}
            >
              <Button
                variant="solid"
                tone="muted"
                surface="dark"
                fluid={isMobile}
                as="button"
                type="button"
                onClick={openCheckout}
              >
                {spiritBox?.buyLabel}
              </Button>
            </div>
          </div>
        </div>

        <StackedChevrons
          href="#features"
          tone="white"
          aria-label={t("scrollToNext")}
          className={[
            "scroll-indicator absolute left-1/2 bottom-12 -translate-x-1/2 transition-opacity duration-300",
            chevronsVisible ? "opacity-100" : "opacity-0 pointer-events-none",
          ].join(" ")}
          onClick={scrollToFeatures}
        />
      </div>

      {/* Feature column. Desktop: empty full-screen placeholders drive the
          scroll length and anchors while the copy lives in the fixed
          crossfade panels (decks pattern); specs/how-to are too tall to fix
          and stay in the flow. Tablet/mobile: everything in the flow. */}
      {panelMode ? (
        <>
          {/* 120vh: the extra 20vh is the breathing gap between panels — the
              previous text has faded and the next hasn't come in yet. Tighter
              than before: headline-only panels read in a beat, so the tour
              keeps a faster, less boring pace. */}
          {FEATURE_SECTIONS.map((s, i) => (
            <section
              key={s.key}
              id={i === 0 ? "features" : undefined}
              data-scroll-anchor={`s${i + 1}`}
              className="h-[120vh]"
            />
          ))}
        </>
      ) : (
        FEATURE_SECTIONS.map((s, i) => (
          <FeatureSection
            key={s.key}
            content={content}
            sectionKey={s.key}
            first={i === 0}
            anchorKey={`s${i + 1}`}
            side={sideFor(i + 1)}
            num={String(i + 1).padStart(2, "0")}
            onOrder={openCheckout}
            ctaLabel={
              s.buyCta
                ? spiritBox?.buyLabel
                : s.ctaTextKey
                  ? text(content, s.ctaTextKey)
                  : undefined
            }
          />
        ))
      )}

      <HowToSection
        content={content}
        anchorKey={`s${FEATURE_SECTIONS.length + 2}`}
        side={panelMode ? "right" : sideFor(FEATURE_SECTIONS.length + 2)}
        tone="dark"
      />

      {panelMode
        ? (["whofor", "included", "notice"] as const).map((key, i) => (
            <section
              key={key}
              data-scroll-anchor={`s${FEATURE_SECTIONS.length + 3 + i}`}
              className="h-[120vh]"
            />
          ))
        : (["whofor", "included", "notice"] as const).map((key, i) => (
            <FeatureSection
              key={key}
              content={content}
              sectionKey={key}
              anchorKey={`s${FEATURE_SECTIONS.length + 3 + i}`}
              side={sideFor(FEATURE_SECTIONS.length + 3 + i)}
              onOrder={openCheckout}
            />
          ))}

      {panelMode ? (
        <RadioTextPanels
          content={content}
          spiritBox={spiritBox}
          onOrder={openCheckout}
          portalHost={portalHost}
          isExiting={isExiting}
        />
      ) : null}

      {/* Order — the radio re-enters on the left half (desktop), so the copy
          takes the right column; on mobile the model rides above the copy. */}
      <section
        id="order"
        data-scroll-anchor="order"
        className="px-6 py-[14vh] md:py-[8vh] md:min-h-screen md:flex md:items-center pointer-events-auto"
      >
        <div className="max-w-6xl mx-auto w-full md:grid md:grid-cols-2 md:gap-10 items-center">
          <div className="hidden md:block" />
          <div className={isMobile ? "pt-[34vh] text-center" : "text-left"}>
            <Reveal>
            <SectionHeading
              title={text(content, "radio.order.title")}
              subtitle={text(content, "radio.order.subtitle")}
              tone="dark"
            />
            <div className={`mt-8 ${isMobile ? "text-left" : ""}`}>
              <MarkdownBody
                body={text(content, "radio.order.body")}
                surface="blue"
              />
            </div>
            {/* Detail launchers sit above the pricing hairline — secondary
                actions before the price/CTA block. */}
            <div
              className={`mt-9 flex flex-wrap gap-3 ${
                isMobile ? "w-full justify-center" : ""
              }`}
            >
              <Button
                variant="outline"
                surface="dark"
                fluid={isMobile}
                as="button"
                type="button"
                onClick={() => setSpecsOpen(true)}
              >
                {text(content, "radio.specs.title")}
              </Button>
              <Button
                variant="outline"
                surface="dark"
                fluid={isMobile}
                as="button"
                type="button"
                onClick={() => setFaqOpen(true)}
              >
                {text(content, "radio.faq.title")}
              </Button>
            </div>
            {/* Pricing foot — mirrors the services service-card foot: a
                hairline divider, a PRICE stat (label + lg price), the shipping
                caption, then the CTA. Tone-aware so it works on the dusk
                desktop and the light tablet/mobile flow. */}
            <div
              className={`service-foot mt-9 ${
                ""
              } ${isMobile ? "!items-center" : ""}`}
            >
              <div
                className={`service-meta-row ${
                  ""
                }`}
              >
                <div className="stat flex flex-col items-start gap-2">
                  <span
                    className={"t-stat-label"}
                  >
                    {text(content, "service.stat.price")}
                  </span>
                  <Price
                    size="lg"
                    inverse
                    cur={currencySymbol(spiritBox?.currency)}
                    unit={spiritBox?.currency ?? "AUD"}
                    usd={spiritBox ? usdAmount(spiritBox.price, rate) ?? undefined : undefined}
                  >
                    {spiritBox ? formatAmount(spiritBox.price) : ""}
                  </Price>
                </div>
                {/* Only render the shipping stat when it has copy — an empty
                    CMS value must not leave a dangling DELIVERY label. */}
                {text(content, "radio.order.shipping") ? (
                  <div className="stat flex flex-col items-start gap-2">
                    <span
                      className={"t-stat-label"}
                    >
                      {text(content, "radio.order.shippingLabel")}
                    </span>
                    <span
                      className={`flex flex-1 items-center max-w-[280px] text-[17px] font-light leading-snug ${
                        "text-white"
                      }`}
                    >
                      {text(content, "radio.order.shipping")}
                    </span>
                  </div>
                ) : null}
              </div>
              <Button
                variant="solid"
                surface="dark"
                fluid={isMobile}
                as="button"
                type="button"
                onClick={openCheckout}
              >
                {spiritBox?.buyLabel}
              </Button>
            </div>
            </Reveal>
          </div>
        </div>
      </section>

      <DeckCheckoutModal
        deckKeys={[SPIRIT_BOX_KEY]}
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        portalHost={portalHost}
      />

      <Modal
        open={specsOpen}
        onClose={() => setSpecsOpen(false)}
        title={text(content, "radio.specs.title")}
        size="wide"
        container={portalHost}
      >
        <SpecsBody content={content} />
      </Modal>
      <Modal
        open={faqOpen}
        onClose={() => setFaqOpen(false)}
        title={text(content, "radio.faq.title")}
        size="wide"
        container={portalHost}
      >
        <FaqBody
          content={content}
          onOrder={() => {
            setFaqOpen(false);
            openCheckout();
          }}
        />
      </Modal>
    </main>
  );
}

/* ── Page shell ── */

type Props = {
  onEl: (el: HTMLDivElement | null) => void;
  onLegalNavigate?: (path: string) => void;
  /** True once the user has requested another page but React hasn't committed
   *  the swap yet (freezes the 3D model under the FBO snapshot). */
  isExiting?: boolean;
};

export function RadioPageContent({ onEl, onLegalNavigate, isExiting }: Props) {
  // Owned portal host (same pattern as DecksPageContent): drei's <Scroll html>
  // keeps its inner ReactDOM root mounted indefinitely, so the checkout modal
  // portals into a node this component removes from the DOM on unmount.
  const [portalHost, setPortalHost] = React.useState<HTMLDivElement | null>(
    null,
  );
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const node = document.createElement("div");
    document.body.appendChild(node);
    setPortalHost(node);
    return () => {
      if (node.parentNode) node.parentNode.removeChild(node);
    };
  }, []);

  const layout = useScrollLayout(radioScrollLayout);
  const pages = pagesFromMeasurement(layout, 24);

  return (
    <ScrollControls pages={pages} damping={0.15}>
      <ScrollElBridge onEl={onEl} />
      <ScrollLayoutMeasurer store={radioScrollLayout} pages={pages} />
      <React.Suspense fallback={null}>
        <RadioPart isExiting={isExiting} />
      </React.Suspense>
      <Scroll html>
        {/* drei's <Scroll html> mounts its children in a detached ReactDOM
            root — re-provide the singleton client for useContent. */}
        <QueryClientProvider client={queryClient}>
          {/* Measured by ScrollLayoutMeasurer: total height drives pages,
              [data-scroll-anchor] offsets drive the model windows. */}
          <div data-scroll-content>
            <RadioHtmlContent portalHost={portalHost} isExiting={isExiting} />
            <div
              data-scroll-anchor="end"
              className="relative z-10 w-screen pointer-events-auto"
            >
              <Footer onLegalNavigate={onLegalNavigate} />
            </div>
          </div>
        </QueryClientProvider>
      </Scroll>
    </ScrollControls>
  );
}
